import React, { useEffect, useMemo, useState } from 'react';
import {
    ChevronDown,
    Calendar,
    Star,
    Send,
    FileCheck,
    Building2,
    ChevronRight,
    Globe,
    Trash2,
    Loader2,
    User,
} from 'lucide-react';
import {
    PipelineStepId,
    ShortlistPhaseId,
    filterApplicationsByScope,
    getApplicationPrimaryStep,
    mergePipelineStepCountsWithHistory,
    historyStageToShortlistPhase,
    applicationToShortlistPhase,
    isDirectWebsiteSollicitatie,
    maxDateIso,
    applicationStatusDisplay,
} from '../../utils/candidateJourneyBuckets';

const PIPELINE_STEPS: { id: PipelineStepId; label: string }[] = [
    { id: 'screening', label: 'Screening vooraf' },
    { id: 'shortlist', label: 'Shortlists' },
    { id: 'voorgesteld', label: 'Voorgesteld' },
    { id: 'negatief_klant', label: 'Negatief klant' },
    { id: 'positief_klant', label: 'Positief klant' },
    { id: 'gesprek', label: 'Gesprek' },
    { id: 'aanbod', label: 'Aanbod' },
    { id: 'plaatsing', label: 'Plaatsing' },
];

type Indicator = { mode: 'empty' } | { mode: 'count'; n: number };

function vacancyTitle(v: any): string {
    return v?.title || 'Onbekende vacature';
}

/** Plaatsing zonder vacancy-join: titel/klant uit placement.data, end_client of directe client-FK. */
function placementDisplayContext(p: any, vac: any) {
    const d = p?.data && typeof p.data === 'object' ? p.data : null;
    const jobOrder = d?.jobOrder && typeof d.jobOrder === 'object' ? d.jobOrder : null;

    let title = vac?.title?.trim();
    if (!title) {
        title =
            jobOrder?.title?.trim() ||
            d?.title?.trim() ||
            (typeof jobOrder?.publicDescription === 'string' ? jobOrder.publicDescription.slice(0, 140).trim() : '') ||
            '';
    }
    if (!title && p?.vacancy_id) {
        title = 'Vacature niet zichtbaar of verwijderd';
    }
    if (!title && p?.number != null && String(p.number).trim() !== '') {
        title = `Plaatsing #${p.number}`;
    }
    if (!title) {
        title = 'Onbekende vacature';
    }

    const opdrachtgever =
        vac?.opdrachtgever ??
        p?.placement_opdrachtgever ??
        (d?.opdrachtgever ? { name: String(d.opdrachtgever) } : null) ??
        (jobOrder?.clientCorporation?.name ? { name: String(jobOrder.clientCorporation.name) } : null);

    const eindklant =
        vac?.eindklant ??
        p?.placement_eindklant ??
        (p?.end_client?.trim() ? { name: p.end_client.trim() } : null) ??
        (d?.clientCorporation?.name ? { name: String(d.clientCorporation.name) } : null);

    return { title, opdrachtgever, eindklant };
}

function formatNlDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatNlDateTime(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Zelfde tabel-layout in alle shortlist-fase-panelen: kolommen lopen visueel gelijk door. */
const ST_TABLE = 'w-full table-fixed border-collapse text-left text-xs min-w-[1020px]';
const ST_TH = (widthAndExtras: string) =>
    `px-3 py-2 font-black text-slate-400 uppercase tracking-widest text-[9px] min-w-0 ${widthAndExtras}`;
const ST_VAC = 'px-3 py-2 align-top min-w-0 w-[20%]';
const ST_VAC_SITE = 'px-3 py-2 align-top min-w-0 w-[14%]';
const ST_HERK = 'px-3 py-2 align-top min-w-0 w-[10%]';
const ST_KAND = 'px-3 py-2 align-middle min-w-0 w-[14%]';
const ST_OG = 'px-3 py-2 align-middle min-w-0 w-[12%]';
const ST_EK = 'px-3 py-2 align-middle min-w-0 w-[12%]';
const ST_STATUS = 'px-3 py-2 align-middle min-w-0 w-[11%]';
const ST_DATUM = 'px-3 py-2 align-middle min-w-0 w-[11%] whitespace-nowrap text-slate-500';
const ST_ACTIES = 'px-3 py-2 align-middle min-w-0 w-[8%] text-center';
const ST_NAV = 'px-3 py-2 align-middle w-10 min-w-[2.5rem] max-w-[2.5rem] text-right';
/** Lege laatste kolom in header (chevron). */
const ST_NAV_HEAD = 'w-10 min-w-[2.5rem] max-w-[2.5rem] p-0';

function unwrapEmbedRow(raw: any): any {
    if (!raw) return null;
    return Array.isArray(raw) ? raw[0] : raw;
}

function resolveCandidateEmbed(app?: any, placement?: any, interview?: any): any {
    return (
        unwrapEmbedRow(app?.candidates) ??
        unwrapEmbedRow(placement?.candidate) ??
        unwrapEmbedRow(interview?.candidates) ??
        null
    );
}

function candidateDisplayLabel(cand: any): string {
    if (!cand) return '—';
    const name = String(cand.name ?? '').trim();
    if (name) return name;
    const num = cand.candidate_number;
    if (num != null && String(num).trim() !== '') return `Kandidaat #${num}`;
    return 'Onbekende kandidaat';
}

export function CandidateCell({ cand }: { cand?: any }) {
    const c = unwrapEmbedRow(cand);
    const [broken, setBroken] = useState(false);
    const label = candidateDisplayLabel(c);
    const photo = c?.photo_url?.trim();
    return (
        <div className="flex items-center gap-1.5 min-w-0 w-full">
            <div className="w-7 h-7 rounded-full border border-slate-200 bg-slate-50 shrink-0 overflow-hidden flex items-center justify-center">
                {photo && !broken ? (
                    <img src={photo} alt="" className="w-full h-full object-cover" onError={() => setBroken(true)} />
                ) : (
                    <User size={12} className="text-slate-400" />
                )}
            </div>
            <span className="text-[10px] font-bold text-slate-800 truncate min-w-0" title={label !== '—' ? label : undefined}>
                {label}
            </span>
        </div>
    );
}

export function ClientCell({ client }: { client?: { name?: string | null; logo_url?: string | null } | null }) {
    const [broken, setBroken] = useState(false);
    const n = client?.name?.trim();
    const logo = client?.logo_url?.trim();
    return (
        <div className="flex items-center gap-1.5 min-w-0 w-full">
            <div className="w-7 h-7 rounded-full border border-slate-200 bg-slate-50 shrink-0 overflow-hidden flex items-center justify-center">
                {logo && !broken ? (
                    <img src={logo} alt="" className="w-full h-full object-cover" onError={() => setBroken(true)} />
                ) : (
                    <Building2 size={12} className="text-slate-400" />
                )}
            </div>
            <span className="text-[10px] font-bold text-slate-700 truncate min-w-0" title={n || undefined}>
                {n || '—'}
            </span>
        </div>
    );
}

function computeStepIndicator(
    step: PipelineStepId,
    stepCounts: Record<PipelineStepId, number>,
    interviewsLen: number,
    plaatsingAccordionTotal?: number
): Indicator {
    let n = stepCounts[step] ?? 0;
    if (step === 'gesprek') n = Math.max(n, interviewsLen);
    if (step === 'plaatsing' && plaatsingAccordionTotal != null && plaatsingAccordionTotal > 0) {
        n = Math.max(n, plaatsingAccordionTotal);
    }
    if (n > 0) return { mode: 'count', n };
    return { mode: 'empty' };
}

export interface CandidatePipelineStripProps {
    applications: any[];
    interviews: any[];
    placements: any[];
    stageHistory?: any[];
    scope: 'current' | 'all';
    /** Klik op een stap: opent Shortlist-tab en scrollt naar de bijbehorende accordion-fase. */
    onOpenShortlistTab: (step: PipelineStepId) => void;
    /** Toon spinners in fase-cirkels i.p.v. lege placeholders. */
    loading?: boolean;
}

/** Horizontale journey-strip: volle breedte detailpaneel; klik opent Shortlist-tab. */
export function CandidatePipelineStrip({
    applications,
    interviews,
    placements,
    stageHistory = [],
    scope,
    onOpenShortlistTab,
    loading = false,
}: CandidatePipelineStripProps) {
    const scopedApps = useMemo(() => filterApplicationsByScope(applications || [], scope), [applications, scope]);
    const stepCounts = useMemo(
        () => mergePipelineStepCountsWithHistory(scopedApps, stageHistory || []),
        [scopedApps, stageHistory]
    );

    const plaatsingAccordionTotal = useMemo(() => {
        const vacIds = new Set((placements || []).map((p: any) => p.vacancy_id).filter(Boolean));
        const extraApps = scopedApps.filter(
            (a) => getApplicationPrimaryStep(a.status) === 'plaatsing' && !vacIds.has(a.vacancy_id)
        ).length;
        return (placements?.length || 0) + extraApps;
    }, [placements, scopedApps]);

    return (
        <div className="group block w-full text-left border-0 border-b border-slate-100 bg-gradient-to-b from-slate-50/95 to-white px-3 py-3 md:px-4 md:py-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]">
            <div className="flex items-center justify-between gap-3 mb-3">
                <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2">
                    Shortlist journey
                    {loading && (
                        <span className="inline-flex items-center gap-1 text-[8px] font-bold normal-case tracking-normal text-slate-500">
                            <Loader2 size={10} className="animate-spin text-[#e51b23]" />
                            Laden…
                        </span>
                    )}
                </p>
                <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                    {loading ? 'Even geduld…' : 'Klik een fase om details te openen'}
                </span>
            </div>

            <div className="relative w-full">
                <div
                    className="pointer-events-none absolute left-[6%] right-[6%] top-[19px] h-px bg-slate-200 hidden sm:block"
                    aria-hidden
                />
                <div className="relative grid w-full grid-cols-4 sm:grid-cols-8 gap-2 sm:gap-1.5 md:gap-2 min-w-0">
                    {PIPELINE_STEPS.map((step) => {
                        const ind = computeStepIndicator(
                            step.id,
                            stepCounts,
                            interviews?.length || 0,
                            plaatsingAccordionTotal
                        );
                        const active = ind.mode !== 'empty';
                        return (
                            <div key={step.id} className="flex flex-col items-center min-w-0 z-[1]">
                                <button
                                    type="button"
                                    disabled={loading}
                                    onClick={() => onOpenShortlistTab(step.id)}
                                    aria-label={`Open shortlist: ${step.label}`}
                                    className={`w-full max-w-[120px] mx-auto rounded-lg border bg-white px-1 py-2 md:py-2.5 flex flex-col items-center gap-1.5 shadow-sm transition-all min-h-[76px] justify-center cursor-pointer hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e51b23]/35 disabled:cursor-wait ${
                                        loading
                                            ? 'border-slate-200 opacity-90'
                                            : active
                                              ? 'border-[#e51b23] ring-1 ring-[#e51b23]/15 hover:bg-red-50/30'
                                              : 'border-slate-200/90 opacity-80 hover:border-slate-300 hover:opacity-100'
                                    }`}
                                >
                                    <div className="h-7 flex items-center justify-center">
                                        {loading ? (
                                            <Loader2
                                                size={22}
                                                className="animate-spin text-[#e51b23]"
                                                aria-label="Laden"
                                            />
                                        ) : ind.mode === 'empty' ? (
                                            <span className="w-6 h-6 rounded-full border border-dashed border-slate-200 bg-slate-50/80" />
                                        ) : (
                                            <span className="min-w-[28px] h-[28px] px-1 rounded-full border-2 border-[#e51b23] text-[#e51b23] text-[11px] font-black flex items-center justify-center tabular-nums bg-white">
                                                {ind.n > 99 ? '99+' : ind.n}
                                            </span>
                                        )}
                                    </div>
                                    <span
                                        className={`text-[7px] sm:text-[8px] font-black uppercase tracking-tight text-center leading-tight px-0.5 ${
                                            active ? 'text-slate-900' : 'text-slate-400'
                                        }`}
                                    >
                                        {step.label}
                                    </span>
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

type PhaseId = ShortlistPhaseId;

export interface CandidateShortlistPhasesPanelProps {
    applications: any[];
    interviews: any[];
    placements: any[];
    /** Fasewijzigingen uit `application_stage_history` (gevuld bij scope Alles). */
    stageHistory?: any[];
    scope: 'current' | 'all';
    onScopeChange: (s: 'current' | 'all') => void;
    onOpenVacancy: (vacancyId: string) => void;
    /** Klik op sollicitatiestatus opent verplaats-modal (niet in klantportaal). */
    onApplicationStatusClick?: (app: any) => void;
    /** Verwijder-knop per sollicitatie; parent toont bevestiging (niet in klantportaal). */
    onApplicationDeleteClick?: (app: any) => void;
    /** Verwijder-knop per plaatsingsrecord. */
    onPlacementDeleteClick?: (placement: any) => void;
    isClientPortal?: boolean;
    /** Na klik op journey-strip: open deze accordion en scroll ernaar. */
    focusPhase?: ShortlistPhaseId | null;
    /** Wijzigt bij elke strip-klik zodat dezelfde fase opnieuw kan focussen. */
    focusPhaseKey?: number;
}

export function CandidateShortlistPhasesPanel({
    applications,
    interviews,
    placements,
    stageHistory = [],
    scope,
    onScopeChange,
    onOpenVacancy,
    onApplicationStatusClick,
    onApplicationDeleteClick,
    onPlacementDeleteClick,
    isClientPortal = false,
    focusPhase = null,
    focusPhaseKey = 0,
}: CandidateShortlistPhasesPanelProps) {
    const scopedApps = useMemo(() => filterApplicationsByScope(applications || [], scope), [applications, scope]);

    const appsById = useMemo(() => {
        const m = new Map<string, any>();
        for (const a of applications || []) {
            if (a?.id) m.set(String(a.id), a);
        }
        return m;
    }, [applications]);

    const siteSollicitatieApps = useMemo(
        () => scopedApps.filter((a) => applicationToShortlistPhase(a) === 'site_sollicitatie'),
        [scopedApps]
    );
    const shortlistApps = useMemo(
        () => scopedApps.filter((a) => applicationToShortlistPhase(a) === 'shortlist'),
        [scopedApps]
    );
    const voorgesteldApps = useMemo(
        () => scopedApps.filter((a) => applicationToShortlistPhase(a) === 'voorgesteld'),
        [scopedApps]
    );
    const aanbodApps = useMemo(
        () => scopedApps.filter((a) => applicationToShortlistPhase(a) === 'aanbod'),
        [scopedApps]
    );
    const plaatsingApps = useMemo(
        () => scopedApps.filter((a) => applicationToShortlistPhase(a) === 'plaatsing'),
        [scopedApps]
    );

    /** Per fase: historische events (niet de huidige fase van de sollicitatie). */
    const historyByPhase = useMemo(() => {
        const buckets: Record<PhaseId, any[]> = {
            site_sollicitatie: [],
            shortlist: [],
            voorgesteld: [],
            gesprek: [],
            aanbod: [],
            plaatsing: [],
        };
        if (scope !== 'all') return buckets;
        for (const h of stageHistory || []) {
            const app = appsById.get(String(h.application_id));
            const phase = historyStageToShortlistPhase(h.stage, app);
            const currentPhase = app ? applicationToShortlistPhase(app) : null;
            const sameAsCurrent =
                app &&
                currentPhase === phase &&
                String(h.stage || '').toLowerCase() === String(app.status || '').toLowerCase();
            if (sameAsCurrent) continue;
            buckets[phase].push(h);
        }
        for (const id of Object.keys(buckets) as PhaseId[]) {
            buckets[id].sort(
                (a, b) => new Date(b.occurred_at || 0).getTime() - new Date(a.occurred_at || 0).getTime()
            );
        }
        return buckets;
    }, [scope, stageHistory, appsById]);

    const placementVacancyIds = useMemo(
        () => new Set((placements || []).map((p: any) => p.vacancy_id).filter(Boolean)),
        [placements]
    );
    const plaatsingAppsWithoutPlacementRow = useMemo(
        () => plaatsingApps.filter((a) => !placementVacancyIds.has(a.vacancy_id)),
        [plaatsingApps, placementVacancyIds]
    );

    /** Meerdere fase-panelen mogen tegelijk open staan (onafhankelijk in-/uitklappen). */
    const [openPhases, setOpenPhases] = useState<Partial<Record<PhaseId, boolean>>>(() => ({
        voorgesteld: true,
    }));

    const phases: {
        id: PhaseId;
        title: string;
        count: number;
        icon: React.ReactNode;
        lastAt: string | null;
    }[] = [
        {
            id: 'site_sollicitatie',
            title: 'Sollicitatie via site',
            count: siteSollicitatieApps.length + (scope === 'all' ? historyByPhase.site_sollicitatie.length : 0),
            icon: <Globe size={14} className="text-sky-600" />,
            lastAt: maxDateIso([
                ...siteSollicitatieApps.map((a) => a.updated_at || a.created_at),
                ...historyByPhase.site_sollicitatie.map((h) => h.occurred_at),
            ]),
        },
        {
            id: 'shortlist',
            title: 'Shortlist',
            count: shortlistApps.length + (scope === 'all' ? historyByPhase.shortlist.length : 0),
            icon: <Star size={14} className="text-amber-500" />,
            lastAt: maxDateIso([
                ...shortlistApps.map((a) => a.updated_at || a.created_at),
                ...historyByPhase.shortlist.map((h) => h.occurred_at),
            ]),
        },
        {
            id: 'voorgesteld',
            title: 'Voorgesteld',
            count: voorgesteldApps.length + (scope === 'all' ? historyByPhase.voorgesteld.length : 0),
            icon: <Send size={14} className="text-violet-500" />,
            lastAt: maxDateIso([
                ...voorgesteldApps.map((a) => a.updated_at || a.created_at),
                ...historyByPhase.voorgesteld.map((h) => h.occurred_at),
            ]),
        },
        {
            id: 'gesprek',
            title: 'Gespreksafspraken',
            count: (interviews || []).length + (scope === 'all' ? historyByPhase.gesprek.length : 0),
            icon: <Calendar size={14} className="text-blue-500" />,
            lastAt: maxDateIso([
                ...(interviews || []).map((i) => i.scheduled_at || i.created_at),
                ...historyByPhase.gesprek.map((h) => h.occurred_at),
            ]),
        },
        {
            id: 'aanbod',
            title: 'Aanbod',
            count: aanbodApps.length + (scope === 'all' ? historyByPhase.aanbod.length : 0),
            icon: <FileCheck size={14} className="text-indigo-600" />,
            lastAt: maxDateIso([
                ...aanbodApps.map((a) => a.updated_at || a.created_at),
                ...historyByPhase.aanbod.map((h) => h.occurred_at),
            ]),
        },
        {
            id: 'plaatsing',
            title: 'Plaatsing',
            count:
                (placements || []).length +
                plaatsingAppsWithoutPlacementRow.length +
                (scope === 'all' ? historyByPhase.plaatsing.length : 0),
            icon: <Star size={14} className="text-emerald-600" />,
            lastAt: maxDateIso([
                ...(placements || []).map((p) => p.created_at),
                ...plaatsingAppsWithoutPlacementRow.map((a) => a.updated_at || a.created_at),
                ...historyByPhase.plaatsing.map((h) => h.occurred_at),
            ]),
        },
    ];

    const togglePhase = (id: PhaseId) => {
        setOpenPhases((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    useEffect(() => {
        if (!focusPhase || focusPhaseKey === 0) return;
        setOpenPhases((prev) => ({ ...prev, [focusPhase]: true }));
        const scrollToPhase = () => {
            document.getElementById(`shortlist-phase-${focusPhase}`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        };
        const t = window.setTimeout(scrollToPhase, 80);
        return () => window.clearTimeout(t);
    }, [focusPhase, focusPhaseKey]);

    const resolveVacFromHistory = (h: any, app?: any) => {
        const raw = h?.vacancies ?? app?.vacancies;
        return Array.isArray(raw) ? raw[0] : raw;
    };

    const renderStageHistoryRows = (phaseId: PhaseId, opts?: { showHerkomst?: boolean }) => {
        if (scope !== 'all') return null;
        const rows = historyByPhase[phaseId] || [];
        return rows.map((h: any) => {
            const app = appsById.get(String(h.application_id));
            const vac = resolveVacFromHistory(h, app);
            const vacId = h.vacancy_id || vac?.id || app?.vacancy_id;
            const prev = h.previous_stage ? String(h.previous_stage).trim() : '';
            const stageLabel = applicationStatusDisplay(h.stage);
            const vacCellClass = opts?.showHerkomst ? ST_VAC_SITE : ST_VAC;
            return (
                <tr
                    key={`hist-${h.id}`}
                    className="hover:bg-amber-50/50 bg-slate-50/80 group border-l-2 border-l-amber-300"
                    onClick={() => vacId && onOpenVacancy(String(vacId))}
                >
                    <td className={`${vacCellClass} font-bold text-slate-800 group-hover:text-[#e51b23]`}>
                        <span className="line-clamp-2">
                            {vac?.vacancy_number != null && (
                                <span className="text-slate-400 font-mono mr-1">#{vac.vacancy_number}</span>
                            )}
                            {vacancyTitle(vac)}
                        </span>
                        <span className="block text-[9px] font-bold text-amber-700 uppercase tracking-wider mt-0.5">
                            Historie
                            {h.changed_by_name ? ` · ${h.changed_by_name}` : ''}
                        </span>
                    </td>
                    {opts?.showHerkomst && <td className={`${ST_HERK} text-[10px] text-slate-500`}>—</td>}
                    <td className={ST_KAND}>
                        <CandidateCell cand={resolveCandidateEmbed(app)} />
                    </td>
                    <td className={ST_OG}>
                        <ClientCell client={vac?.opdrachtgever} />
                    </td>
                    <td className={ST_EK}>
                        <ClientCell client={vac?.eindklant} />
                    </td>
                    <td className={ST_STATUS}>
                        <span className="inline-flex flex-col gap-0.5 items-start">
                            <span className="inline-flex px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border bg-amber-50 text-amber-800 border-amber-200">
                                {stageLabel}
                                {prev ? ` ← ${applicationStatusDisplay(prev)}` : ''}
                            </span>
                            {app?.status && (
                                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">
                                    Nu: {applicationStatusDisplay(app.status)}
                                </span>
                            )}
                        </span>
                    </td>
                    <td className={ST_DATUM}>{formatNlDateTime(h.occurred_at)}</td>
                    <td className={ST_ACTIES} aria-hidden />
                    <td className={ST_NAV}>
                        <ChevronRight size={14} className="text-slate-200 group-hover:text-[#e51b23] inline" />
                    </td>
                </tr>
            );
        });
    };

    return (
        <div className="space-y-4 animate-fadeIn">
            <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <div>
                    <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">Shortlist &amp; fases</h3>
                    <p className="text-[10px] text-slate-500 font-medium mt-1 max-w-xl">
                        Site-sollicitaties (careers), shortlist, voorstellen, afspraken, aanbod en plaatsingen — met
                        logo&apos;s van opdrachtgever en eindklant. Bij <strong>Alles</strong> ook eerdere fasestappen (regels met gele rand).
                    </p>
                </div>
                <div className="flex rounded-lg border border-slate-200 overflow-hidden shrink-0">
                    <button
                        type="button"
                        onClick={() => onScopeChange('current')}
                        className={`px-3 py-2 text-[9px] font-black uppercase tracking-widest transition-colors ${
                            scope === 'current' ? 'bg-[#185ABD] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                        }`}
                    >
                        Huidige
                    </button>
                    <button
                        type="button"
                        onClick={() => onScopeChange('all')}
                        className={`px-3 py-2 text-[9px] font-black uppercase tracking-widest transition-colors ${
                            scope === 'all' ? 'bg-[#185ABD] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                        }`}
                    >
                        Alles
                    </button>
                </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm divide-y divide-slate-100">
                {phases.map((ph) => {
                    const isOpen = !!openPhases[ph.id];
                    return (
                        <div
                            key={ph.id}
                            id={`shortlist-phase-${ph.id}`}
                            className={`scroll-mt-4 ${isOpen ? 'bg-slate-50/80' : 'bg-white'}`}
                        >
                            <button
                                type="button"
                                aria-expanded={isOpen}
                                onClick={() => togglePhase(ph.id)}
                                className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50/90 ${
                                    isOpen ? 'border-t-2 border-t-[#e51b23]' : ''
                                }`}
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="p-1.5 rounded-lg bg-slate-100 border border-slate-200 shrink-0">
                                        {ph.icon}
                                    </span>
                                    <span className="text-[11px] font-black text-slate-800 uppercase tracking-wide truncate">
                                        {ph.title}{' '}
                                        <span className="text-slate-400 font-bold">({ph.count})</span>
                                    </span>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    {ph.lastAt && (
                                        <span className="hidden sm:block text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                                            Laatste activiteit {formatNlDate(ph.lastAt)}
                                        </span>
                                    )}
                                    <ChevronDown
                                        size={16}
                                        className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                                    />
                                </div>
                            </button>

                            {isOpen && (
                                <div className="px-2 pb-3 md:px-4 md:pb-4">
                                    {ph.id === 'gesprek' && (
                                        <div className="mt-2 overflow-x-auto custom-scrollbar rounded-lg border border-slate-200 bg-white">
                                            {(interviews || []).length === 0 && historyByPhase.gesprek.length === 0 ? (
                                                <p className="text-center py-8 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                                    Geen afspraken
                                                </p>
                                            ) : (
                                                <table className={ST_TABLE}>
                                                    <thead className="bg-slate-50 border-b border-slate-100">
                                                        <tr>
                                                            <th className={ST_TH('w-[20%] text-left')}>Vacature</th>
                                                            <th className={ST_TH('w-[14%] whitespace-nowrap')}>Kandidaat</th>
                                                            <th className={ST_TH('w-[12%] whitespace-nowrap')}>Opdrachtgever</th>
                                                            <th className={ST_TH('w-[12%] whitespace-nowrap')}>Eindklant</th>
                                                            <th className={ST_TH('w-[11%]')}>Status</th>
                                                            <th className={ST_TH('w-[11%]')}>Datum</th>
                                                            <th className={ST_TH('w-[8%] text-center')}>Acties</th>
                                                            <th className={ST_NAV_HEAD} aria-hidden />
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-50">
                                                        {(interviews || []).map((row: any) => {
                                                            const vac = row.vacancies;
                                                            const linkedApp = row.application_id
                                                                ? appsById.get(String(row.application_id))
                                                                : applications.find(
                                                                      (a) =>
                                                                          String(a.vacancy_id) === String(row.vacancy_id) &&
                                                                          String(a.candidate_id) === String(row.candidate_id)
                                                                  );
                                                            return (
                                                                <tr
                                                                    key={row.id}
                                                                    className="hover:bg-slate-50 cursor-pointer group"
                                                                    onClick={() => vac?.id && onOpenVacancy(vac.id)}
                                                                >
                                                                    <td className={`${ST_VAC} font-bold text-slate-900 group-hover:text-[#e51b23]`}>
                                                                        <span className="line-clamp-2">
                                                                            {vac?.vacancy_number != null && (
                                                                                <span className="text-slate-400 font-mono mr-1">
                                                                                    #{vac.vacancy_number}
                                                                                </span>
                                                                            )}
                                                                            {vacancyTitle(vac)}
                                                                        </span>
                                                                        {row.location && String(row.location).trim() !== '' && (
                                                                            <span className="block text-[9px] font-medium text-slate-500 truncate mt-0.5">
                                                                                {row.location}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td className={ST_KAND}>
                                                                        <CandidateCell
                                                                            cand={resolveCandidateEmbed(linkedApp, undefined, row)}
                                                                        />
                                                                    </td>
                                                                    <td className={ST_OG}>
                                                                        <ClientCell client={vac?.opdrachtgever} />
                                                                    </td>
                                                                    <td className={ST_EK}>
                                                                        <ClientCell client={vac?.eindklant} />
                                                                    </td>
                                                                    <td className={ST_STATUS}>
                                                                        <span className="inline-flex px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border bg-blue-50 text-blue-700 border-blue-100">
                                                                            {row.status || 'Scheduled'}
                                                                        </span>
                                                                    </td>
                                                                    <td className={ST_DATUM}>{formatNlDateTime(row.scheduled_at)}</td>
                                                                    <td className={ST_ACTIES} aria-hidden />
                                                                    <td className={ST_NAV}>
                                                                        <ChevronRight
                                                                            size={14}
                                                                            className="text-slate-200 group-hover:text-[#e51b23] inline"
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                        {renderStageHistoryRows('gesprek')}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    )}

                                    {ph.id === 'plaatsing' && (
                                        <div className="mt-2 overflow-x-auto custom-scrollbar rounded-lg border border-slate-200 bg-white">
                                            {(placements || []).length === 0 &&
                                            plaatsingAppsWithoutPlacementRow.length === 0 &&
                                            historyByPhase.plaatsing.length === 0 ? (
                                                <p className="text-center py-8 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                                    Geen plaatsingen
                                                </p>
                                            ) : (
                                                <table className={ST_TABLE}>
                                                    <thead className="bg-slate-50 border-b border-slate-100">
                                                        <tr>
                                                            <th className={ST_TH('w-[20%] text-left')}>Vacature</th>
                                                            <th className={ST_TH('w-[14%] whitespace-nowrap')}>Kandidaat</th>
                                                            <th className={ST_TH('w-[12%] whitespace-nowrap')}>Opdrachtgever</th>
                                                            <th className={ST_TH('w-[12%] whitespace-nowrap')}>Eindklant</th>
                                                            <th className={ST_TH('w-[11%]')}>Status</th>
                                                            <th className={ST_TH('w-[11%]')}>Datum</th>
                                                            <th className={ST_TH('w-[8%] text-center')}>Acties</th>
                                                            <th className={ST_NAV_HEAD} aria-hidden />
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-50">
                                                        {(placements || []).map((p: any) => {
                                                            const vac = p.vacancies;
                                                            const ctx = placementDisplayContext(p, vac);
                                                            return (
                                                                <tr
                                                                    key={`p-${p.id}`}
                                                                    className="hover:bg-slate-50 cursor-pointer group"
                                                                    onClick={() => {
                                                                        const vid = vac?.id || p.vacancy_id;
                                                                        if (vid) onOpenVacancy(vid);
                                                                    }}
                                                                >
                                                                    <td className={`${ST_VAC} font-bold text-slate-900 group-hover:text-[#e51b23]`}>
                                                                        <span className="line-clamp-2">
                                                                            {vac?.vacancy_number != null && (
                                                                                <span className="text-slate-400 font-mono mr-1">
                                                                                    #{vac.vacancy_number}
                                                                                </span>
                                                                            )}
                                                                            {!vac?.vacancy_number &&
                                                                                p.number != null &&
                                                                                String(p.number).trim() !== '' && (
                                                                                    <span className="text-slate-400 font-mono mr-1">
                                                                                        #{p.number}
                                                                                    </span>
                                                                                )}
                                                                            {ctx.title}
                                                                        </span>
                                                                    </td>
                                                                    <td className={ST_KAND}>
                                                                        <CandidateCell cand={resolveCandidateEmbed(undefined, p)} />
                                                                    </td>
                                                                    <td className={ST_OG}>
                                                                        <ClientCell client={ctx.opdrachtgever} />
                                                                    </td>
                                                                    <td className={ST_EK}>
                                                                        <ClientCell client={ctx.eindklant} />
                                                                    </td>
                                                                    <td className={ST_STATUS}>
                                                                        <span className="inline-flex px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border bg-emerald-50 text-emerald-700 border-emerald-100">
                                                                            {p.status || '—'}
                                                                        </span>
                                                                    </td>
                                                                    <td className={ST_DATUM}>{formatNlDate(p.created_at)}</td>
                                                                    <td
                                                                        className={ST_ACTIES}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        {!isClientPortal && onPlacementDeleteClick ? (
                                                                            <button
                                                                                type="button"
                                                                                title="Plaatsing verwijderen"
                                                                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                                                                onClick={() => onPlacementDeleteClick(p)}
                                                                            >
                                                                                <Trash2 size={14} />
                                                                            </button>
                                                                        ) : null}
                                                                    </td>
                                                                    <td className={ST_NAV}>
                                                                        <ChevronRight
                                                                            size={14}
                                                                            className="text-slate-200 group-hover:text-[#e51b23] inline"
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                        {plaatsingAppsWithoutPlacementRow.map((app: any) => {
                                                            const vac = app.vacancies;
                                                            return (
                                                                <tr
                                                                    key={`a-${app.id}`}
                                                                    className="hover:bg-slate-50 cursor-pointer group"
                                                                    onClick={() => app.vacancy_id && onOpenVacancy(app.vacancy_id)}
                                                                >
                                                                    <td className={`${ST_VAC} font-bold text-slate-900 group-hover:text-[#e51b23]`}>
                                                                        <span className="line-clamp-2">
                                                                            {vac?.vacancy_number != null && (
                                                                                <span className="text-slate-400 font-mono mr-1">
                                                                                    #{vac.vacancy_number}
                                                                                </span>
                                                                            )}
                                                                            {vacancyTitle(vac)}
                                                                        </span>
                                                                        <span className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">
                                                                            Via sollicitatie
                                                                        </span>
                                                                    </td>
                                                                    <td className={ST_KAND}>
                                                                        <CandidateCell cand={resolveCandidateEmbed(app)} />
                                                                    </td>
                                                                    <td className={ST_OG}>
                                                                        <ClientCell client={vac?.opdrachtgever} />
                                                                    </td>
                                                                    <td className={ST_EK}>
                                                                        <ClientCell client={vac?.eindklant} />
                                                                    </td>
                                                                    <td className={ST_STATUS} onClick={(e) => e.stopPropagation()}>
                                                                        {onApplicationStatusClick && !isClientPortal ? (
                                                                            <button
                                                                                type="button"
                                                                                title="Status wijzigen"
                                                                                className="inline-flex px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100 transition-colors cursor-pointer"
                                                                                onClick={() => onApplicationStatusClick(app)}
                                                                            >
                                                                                {applicationStatusDisplay(app.status)}
                                                                            </button>
                                                                        ) : (
                                                                            <span className="inline-flex px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border bg-emerald-50 text-emerald-700 border-emerald-100">
                                                                                {applicationStatusDisplay(app.status)}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td className={ST_DATUM}>
                                                                        {formatNlDate(app.updated_at || app.created_at)}
                                                                    </td>
                                                                    <td
                                                                        className={ST_ACTIES}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        {!isClientPortal && onApplicationDeleteClick ? (
                                                                            <button
                                                                                type="button"
                                                                                title="Sollicitatie verwijderen"
                                                                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                                                                onClick={() => onApplicationDeleteClick(app)}
                                                                            >
                                                                                <Trash2 size={14} />
                                                                            </button>
                                                                        ) : null}
                                                                    </td>
                                                                    <td className={ST_NAV}>
                                                                        <ChevronRight
                                                                            size={14}
                                                                            className="text-slate-200 group-hover:text-[#e51b23] inline"
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                        {renderStageHistoryRows('plaatsing')}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    )}

                                    {(ph.id === 'site_sollicitatie' ||
                                        ph.id === 'shortlist' ||
                                        ph.id === 'voorgesteld' ||
                                        ph.id === 'aanbod') && (
                                        <div className="mt-2 overflow-x-auto custom-scrollbar rounded-lg border border-slate-200 bg-white">
                                            {(() => {
                                                const phaseApps =
                                                    ph.id === 'site_sollicitatie'
                                                        ? siteSollicitatieApps
                                                        : ph.id === 'shortlist'
                                                          ? shortlistApps
                                                          : ph.id === 'voorgesteld'
                                                            ? voorgesteldApps
                                                            : aanbodApps;
                                                const histN = historyByPhase[ph.id]?.length ?? 0;
                                                return phaseApps.length === 0 && histN === 0;
                                            })() ? (
                                                <p className="text-center py-8 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                                    Geen items
                                                </p>
                                            ) : (
                                                <table className={ST_TABLE}>
                                                    <thead className="bg-slate-50 border-b border-slate-100">
                                                        <tr>
                                                            <th
                                                                className={ST_TH(
                                                                    ph.id === 'site_sollicitatie'
                                                                        ? 'w-[14%] text-left'
                                                                        : 'w-[20%] text-left'
                                                                )}
                                                            >
                                                                Vacature
                                                            </th>
                                                            {ph.id === 'site_sollicitatie' && (
                                                                <th className={ST_TH('w-[10%]')}>Herkomst</th>
                                                            )}
                                                            <th className={ST_TH('w-[14%] whitespace-nowrap')}>Kandidaat</th>
                                                            <th className={ST_TH('w-[12%] whitespace-nowrap')}>Opdrachtgever</th>
                                                            <th className={ST_TH('w-[12%] whitespace-nowrap')}>Eindklant</th>
                                                            <th className={ST_TH('w-[11%]')}>Status</th>
                                                            <th className={ST_TH('w-[11%]')}>Datum</th>
                                                            <th className={ST_TH('w-[8%] text-center')}>Acties</th>
                                                            <th className={ST_NAV_HEAD} aria-hidden />
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-50">
                                                        {(ph.id === 'site_sollicitatie'
                                                            ? siteSollicitatieApps
                                                            : ph.id === 'shortlist'
                                                              ? shortlistApps
                                                              : ph.id === 'voorgesteld'
                                                                ? voorgesteldApps
                                                                : aanbodApps
                                                        ).map((app: any) => {
                                                            const vac = app.vacancies;
                                                            const herkomst = String(app.cv_summary || '')
                                                                .replace(/\s+/g, ' ')
                                                                .trim();
                                                            const vacCellClass =
                                                                ph.id === 'site_sollicitatie' ? ST_VAC_SITE : ST_VAC;
                                                            return (
                                                                <tr
                                                                    key={app.id}
                                                                    className="hover:bg-slate-50 cursor-pointer group"
                                                                    onClick={() => app.vacancy_id && onOpenVacancy(app.vacancy_id)}
                                                                >
                                                                    <td className={`${vacCellClass} font-bold text-slate-900 group-hover:text-[#e51b23]`}>
                                                                        <span className="line-clamp-2">
                                                                            {vac?.vacancy_number != null && (
                                                                                <span className="text-slate-400 font-mono mr-1">
                                                                                    #{vac.vacancy_number}
                                                                                </span>
                                                                            )}
                                                                            {vacancyTitle(vac)}
                                                                        </span>
                                                                    </td>
                                                                    {ph.id === 'site_sollicitatie' && (
                                                                        <td className={`${ST_HERK} text-[10px] text-slate-600`}>
                                                                            <span className="line-clamp-2" title={herkomst || undefined}>
                                                                                {herkomst
                                                                                    ? herkomst.length > 90
                                                                                        ? `${herkomst.slice(0, 90)}…`
                                                                                        : herkomst
                                                                                    : '—'}
                                                                            </span>
                                                                        </td>
                                                                    )}
                                                                    <td className={ST_KAND}>
                                                                        <CandidateCell cand={resolveCandidateEmbed(app)} />
                                                                    </td>
                                                                    <td className={ST_OG}>
                                                                        <ClientCell client={vac?.opdrachtgever} />
                                                                    </td>
                                                                    <td className={ST_EK}>
                                                                        <ClientCell client={vac?.eindklant} />
                                                                    </td>
                                                                    <td className={ST_STATUS} onClick={(e) => e.stopPropagation()}>
                                                                        {onApplicationStatusClick && !isClientPortal ? (
                                                                            <button
                                                                                type="button"
                                                                                title="Status wijzigen"
                                                                                className="inline-flex px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100 transition-colors cursor-pointer"
                                                                                onClick={() => onApplicationStatusClick(app)}
                                                                            >
                                                                                {applicationStatusDisplay(app.status)}
                                                                            </button>
                                                                        ) : (
                                                                            <span className="inline-flex px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border bg-slate-50 text-slate-700 border-slate-200">
                                                                                {applicationStatusDisplay(app.status)}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td className={ST_DATUM}>
                                                                        {formatNlDate(app.updated_at || app.created_at)}
                                                                    </td>
                                                                    <td
                                                                        className={ST_ACTIES}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        {!isClientPortal && onApplicationDeleteClick ? (
                                                                            <button
                                                                                type="button"
                                                                                title="Sollicitatie verwijderen"
                                                                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                                                                onClick={() => onApplicationDeleteClick(app)}
                                                                            >
                                                                                <Trash2 size={14} />
                                                                            </button>
                                                                        ) : null}
                                                                    </td>
                                                                    <td className={ST_NAV}>
                                                                        <ChevronRight
                                                                            size={14}
                                                                            className="text-slate-200 group-hover:text-[#e51b23] inline"
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                        {renderStageHistoryRows(ph.id, {
                                                            showHerkomst: ph.id === 'site_sollicitatie',
                                                        })}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function unwrapClientEmbed(raw: any): { id?: string; name?: string; logo_url?: string } | null {
    if (!raw) return null;
    const c = Array.isArray(raw) ? raw[0] : raw;
    return c?.name || c?.logo_url ? c : c?.id ? c : null;
}

function vacancyStatusLabel(status: string | null | undefined): string {
    const s = String(status || '').trim();
    if (!s) return '—';
    const l = s.toLowerCase();
    if (['open', 'doing', 'actief', 'interview', 'sourcing'].includes(l)) return 'Open';
    if (l.includes('closed') || l === 'gesloten') return 'Gesloten';
    if (l.includes('afgerond') || l === 'completed') return 'Afgerond';
    if (l === 'concept') return 'Concept';
    return s;
}

function vacancyStatusBadgeClass(status: string | null | undefined): string {
    const l = vacancyStatusLabel(status).toLowerCase();
    if (l === 'open') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    if (l === 'gesloten') return 'bg-red-50 text-red-600 border-red-100';
    if (l === 'afgerond') return 'bg-slate-100 text-slate-600 border-slate-200';
    if (l === 'concept') return 'bg-amber-50 text-amber-700 border-amber-100';
    return 'bg-slate-50 text-slate-600 border-slate-200';
}

export interface ClientVacancyListTableProps {
    vacancies: any[];
    onOpenVacancy: (vacancyId: string) => void;
    resolveClient?: (clientId: string | null | undefined, embed: any) => { name?: string; logo_url?: string } | null;
    emptyLabel?: string;
}

/** Flat vacancy list — zelfde kolommen/styling als shortlist-fasetabellen. */
export function ClientVacancyListTable({
    vacancies,
    onOpenVacancy,
    resolveClient,
    emptyLabel = 'Geen vacatures gevonden.',
}: ClientVacancyListTableProps) {
    const resolve = (id: string | null | undefined, embed: any) => {
        const u = unwrapClientEmbed(embed);
        if (u?.name || u?.logo_url) return u;
        if (resolveClient) return resolveClient(id, embed);
        return u;
    };

    if (!vacancies.length) {
        return (
            <p className="text-center py-10 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                {emptyLabel}
            </p>
        );
    }

    return (
        <div className="overflow-x-auto custom-scrollbar rounded-lg border border-slate-200 bg-white">
            <table className={ST_TABLE}>
                <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                        <th className={ST_TH('w-[28%] text-left')}>Vacature</th>
                        <th className={ST_TH('w-[14%] whitespace-nowrap')}>Opdrachtgever</th>
                        <th className={ST_TH('w-[14%] whitespace-nowrap')}>Eindklant</th>
                        <th className={ST_TH('w-[12%]')}>Status</th>
                        <th className={ST_TH('w-[12%]')}>Datum</th>
                        <th className={ST_TH('w-[10%] text-center')}>Acties</th>
                        <th className={ST_NAV_HEAD} aria-hidden />
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                    {vacancies.map((v) => {
                        const og = resolve(v.opdrachtgever_id, v.opdrachtgever);
                        const ek = resolve(v.eindklant_id, v.eindklant);
                        const loc = v.location || v.data?.location;
                        return (
                            <tr
                                key={v.id}
                                className="hover:bg-slate-50 cursor-pointer group"
                                onClick={() => v.id && onOpenVacancy(v.id)}
                            >
                                <td className={`${ST_VAC} font-bold text-slate-900 group-hover:text-[#e51b23]`}>
                                    <span className="line-clamp-2">
                                        {v.vacancy_number != null && v.vacancy_number !== '' && (
                                            <span className="text-slate-400 font-mono mr-1">#{v.vacancy_number}</span>
                                        )}
                                        {vacancyTitle(v)}
                                    </span>
                                    {loc && String(loc).trim() !== '' && String(loc) !== '—' && (
                                        <span className="block text-[9px] font-medium text-slate-500 truncate mt-0.5">
                                            {loc}
                                        </span>
                                    )}
                                </td>
                                <td className={ST_OG}>
                                    <ClientCell client={og} />
                                </td>
                                <td className={ST_EK}>
                                    <ClientCell client={ek} />
                                </td>
                                <td className={ST_STATUS}>
                                    <span
                                        className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${vacancyStatusBadgeClass(v.status)}`}
                                    >
                                        {vacancyStatusLabel(v.status)}
                                    </span>
                                </td>
                                <td className={ST_DATUM}>{formatNlDate(v.created_at)}</td>
                                <td className={ST_ACTIES} aria-hidden />
                                <td className={ST_NAV}>
                                    <ChevronRight size={14} className="text-slate-200 group-hover:text-[#e51b23] inline" />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
